import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { ClientActions } from '../actions/client/client.actions';
import { MandateActions } from '../actions/mandate/mandate.actions';
import { TombstoneActions } from '../actions/tombstone/tombstone.actions';

import type { Client } from '../interfaces/client/client.interface';
import type { Mandate } from '../interfaces/client/mandate.interface';
import type { Tombstone } from '../interfaces/client/tombstone.interface';
import type { User } from '../interfaces/user/user.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import ClientMark from './components/ClientMark';
import StageChip from './components/StageChip';
import KvRow from './components/KvRow';
import { ClientTier } from './components/ClientCard';

import ClientEditModal from './ClientEditModal';
import CreateMandateModal from './CreateMandateModal';
import CreateTombstoneModal from './CreateTombstoneModal';

import {
  num,
  formatZar,
  yearOf,
  formatDate,
  formatMonthYear,
  initialsFromName,
  deriveTier,
  deriveStage,
  deriveCode,
  deriveId,
  deriveTombstoneType,
  deriveTombstoneRole
} from './lib/mandate-helpers';

// =============================================================================
//  Client detail — v3 redesign
//
//  Mirrors the Mandate-detail pattern (hero, stats row, tabs, two-column body)
//  but populates client-centric info: active mandates, closed tombstones,
//  engagement team, key relationship metadata.
//
//  Backend gaps reflected:
//    - tier (Strategic/Growth/Emerging) → stubbed from id hash
//    - "lead partner" → uses first personnel entry
//    - Contacts / Documents / Notes tabs are stubbed (legacy /clients/:id has them)
// =============================================================================

type TabKey = 'overview' | 'mandates' | 'tombstones' | 'contacts' | 'documents' | 'notes';

const TABS: ReadonlyArray<{ key: TabKey; label: string; ready: boolean }> = [
  { key: 'overview', label: 'Overview', ready: true },
  // Mandates + Tombstones tabs render from already-loaded
  // clientMandates / clientTombstones state.  Backend data is the same as
  // the Overview cards, just shown as a full table for browsing the long
  // tail (Overview shows only the first 8).
  { key: 'mandates', label: 'Mandates', ready: true },
  { key: 'tombstones', label: 'Tombstones', ready: true },
  { key: 'contacts', label: 'Contacts', ready: false },
  { key: 'documents', label: 'Documents', ready: false },
  { key: 'notes', label: 'Notes', ready: false }
];

const TIER_TONE: Record<ClientTier, string> = {
  Strategic: 'gold',
  Growth: 'info',
  Emerging: 'muted'
};

const leadName = (personnel: Client['personnel']): string | undefined => {
  if (!personnel || typeof personnel === 'string' || personnel.length === 0) return undefined;
  const first = personnel[0] as User & { full_name?: string };
  if (first.full_name) return first.full_name;
  if (first.first_name || first.last_name)
    return `${first.first_name ?? ''} ${first.last_name ?? ''}`.trim();
  return undefined;
};

const ClientDetailV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const clients: Client[] = useAppSelector(({ client_store }) => client_store.clients) || [];
  const mandates: Mandate[] = useAppSelector(({ mandate_store }) => mandate_store.mandates) || [];
  const tombstones: Tombstone[] =
    useAppSelector(({ tombstone_store }) => tombstone_store.tombstones) || [];

  const [tab, setTab] = useState<TabKey>('overview');
  const [editing, setEditing] = useState(false);
  const [creatingMandate, setCreatingMandate] = useState(false);
  const [creatingTombstone, setCreatingTombstone] = useState(false);

  useEffect(() => {
    dispatch(ClientActions.loadClients(noOp));
    dispatch(MandateActions.loadMandates(noOp));
    dispatch(TombstoneActions.loadTombstones(noOp));
  }, [dispatch]);

  const client = useMemo(() => clients.find((c) => String(c.id) === String(id)), [clients, id]);

  // Derived counters --------------------------------------------------------
  const clientMandates = useMemo(
    () => mandates.filter((m) => String(m.client_id) === String(id)),
    [mandates, id]
  );
  const clientTombstones = useMemo(
    () => tombstones.filter((t) => String(t.client_id) === String(id)),
    [tombstones, id]
  );

  // Loading / not-found states ---------------------------------------------
  if (!client && clients.length === 0) {
    return (
      <>
        <Topbar crumbs={['Deal Flow', 'Clients', '…']} />
        <div className="page">
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Loading client…
          </div>
        </div>
      </>
    );
  }
  if (!client) {
    return (
      <>
        <Topbar crumbs={['Deal Flow', 'Clients', 'Not found']} />
        <div className="page">
          <button className="back-link" type="button" onClick={() => navigate('/clients')}>
            <Icon name="back" size={14} />
            All clients
          </button>
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Client <strong>#{id}</strong> not found.
          </div>
        </div>
      </>
    );
  }

  // Derived display values --------------------------------------------------
  const tier: ClientTier = deriveTier(client);
  const lead = leadName(client.personnel);
  const activeMandates = clientMandates.filter((m) => deriveStage(m) !== 'Closed');
  const closedDeals =
    clientTombstones.length + clientMandates.filter((m) => deriveStage(m) === 'Closed').length;

  const totalTransactionValue =
    clientMandates.reduce((sum, m) => sum + num(m.transaction_size), 0) +
    clientTombstones.reduce((sum, t) => sum + num(t.size_of_transaction), 0);

  const teamSize = Array.isArray(client.personnel) ? client.personnel.length : 0;
  const since = yearOf(client.created_at);

  return (
    <>
      <Topbar
        crumbs={['Deal Flow', 'Clients', client.name || 'Client']}
        tools={
          <>
            <button className="btn ghost" type="button" onClick={() => navigate('/clients')}>
              <Icon name="back" size={13} />
              All clients
            </button>
            {/*
              Opens the same CreateMandateModal used on /mandates + the
              dashboard, but with this client pre-selected via
              `defaultClientId` so the user doesn't have to find them in
              the long dropdown.  After successful create, the modal
              navigates straight to /mandates/<new-id>.
            */}
            <button className="btn ghost" type="button" onClick={() => setCreatingMandate(true)}>
              <Icon name="plus" size={13} />
              New mandate
            </button>
            <button className="btn primary" type="button" onClick={() => setEditing(true)}>
              <Icon name="check" size={13} />
              Edit
            </button>
          </>
        }
      />
      {editing && <ClientEditModal client={client} onClose={() => setEditing(false)} />}
      {creatingMandate && (
        <CreateMandateModal defaultClientId={client.id} onClose={() => setCreatingMandate(false)} />
      )}
      {creatingTombstone && (
        <CreateTombstoneModal
          defaultClientId={client.id}
          onClose={() => setCreatingTombstone(false)}
        />
      )}
      <div className="page">
        {/* --- Hero card -------------------------------------------------- */}
        <div className="detail-hero">
          <div className="detail-hero-top">
            <ClientMark name={client.name} />
            <div className="meta">
              <div className="chips">
                <span className={`chip ${TIER_TONE[tier]}`}>{tier}</span>
                {client.client_type && <span className="chip muted">{client.client_type}</span>}
                {client.category && <span className="chip muted">{client.category}</span>}
              </div>
              <h1>{client.name}</h1>
              <div className="sub">
                {[
                  client.sector,
                  client.geographic_focus || client.location,
                  lead && `Lead: ${lead}`
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Client'}
              </div>
            </div>
            <div className="value">
              <div className="eyebrow">Total transaction value</div>
              <div className="v">{formatZar(totalTransactionValue)}</div>
              <div className="sub">Since {since}</div>
            </div>
          </div>

          <div className="detail-hero-stats">
            <div className="stat">
              <div className="label">Active mandates</div>
              <div className="value">{activeMandates.length}</div>
            </div>
            <div className="stat">
              <div className="label">Closed deals</div>
              <div className="value">{closedDeals}</div>
            </div>
            <div className="stat">
              <div className="label">Tombstones</div>
              <div className="value">{clientTombstones.length}</div>
            </div>
            <div className="stat">
              <div className="label">Team members</div>
              <div className="value">{teamSize}</div>
            </div>
          </div>
        </div>

        {/* --- Tabs ------------------------------------------------------- */}
        {/* See MandateDetail for the rationale — clickable stub tabs > greyed-out. */}
        <div className="tab-strip">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab ${tab === t.key ? 'active' : ''} ${!t.ready ? 'stub' : ''}`}
              onClick={() => setTab(t.key)}
              title={t.ready ? undefined : 'Coming soon — legacy /clients/:id still has this data'}
            >
              {!t.ready && (
                <span aria-hidden style={{ opacity: 0.4 }}>
                  ·&nbsp;
                </span>
              )}
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="grid detail">
            {/* --- LEFT column -------------------------------------------- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card flush">
                <div className="card-h">
                  <h3>Active mandates</h3>
                  <div className="actions">
                    <button
                      className="btn ghost"
                      type="button"
                      onClick={() => navigate('/mandates')}
                    >
                      View all
                      <Icon name="arrow" size={13} />
                    </button>
                  </div>
                </div>
                {activeMandates.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}
                  >
                    No active mandates with this client.
                  </div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Type</th>
                        <th>Stage</th>
                        <th className="right">Value</th>
                        <th>Target close</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeMandates.slice(0, 8).map((m) => (
                        <tr
                          key={m.id}
                          onClick={() => navigate(`/mandates/${m.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="strong">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span>{deriveCode(m)}</span>
                              <span
                                className="mono"
                                style={{ fontSize: 11, color: 'var(--ink-3)' }}
                              >
                                {deriveId(m)}
                              </span>
                            </div>
                          </td>
                          <td>{m.transaction_type || '—'}</td>
                          <td>
                            <StageChip stage={deriveStage(m)} />
                          </td>
                          <td className="num right strong">{formatZar(m.transaction_size)}</td>
                          <td className="mono">{formatDate(m.expected_end_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="card flush">
                <div className="card-h">
                  <h3>Track record</h3>
                  <div className="actions">
                    <button
                      className="btn ghost"
                      type="button"
                      onClick={() => navigate('/tombstones')}
                    >
                      View all
                      <Icon name="arrow" size={13} />
                    </button>
                  </div>
                </div>
                {clientTombstones.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}
                  >
                    No closed transactions yet.
                  </div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Type</th>
                        <th>BSM role</th>
                        <th className="right">Value</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...clientTombstones]
                        .sort(
                          (a, b) =>
                            (new Date(b.date).getTime() || 0) - (new Date(a.date).getTime() || 0)
                        )
                        .slice(0, 6)
                        .map((t) => (
                          <tr key={t.id}>
                            <td className="num">{yearOf(t.date)}</td>
                            <td>
                              <span className="chip gold">{deriveTombstoneType(t)}</span>
                            </td>
                            <td>{deriveTombstoneRole(t)}</td>
                            <td className="num right strong">{formatZar(t.size_of_transaction)}</td>
                            <td className="mono">{formatMonthYear(t.date)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* --- RIGHT column ------------------------------------------- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Engagement team
                </h3>
                {!Array.isArray(client.personnel) || client.personnel.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>
                    No team members assigned.
                  </div>
                ) : (
                  (client.personnel as User[]).map((p, i) => {
                    const pp = p as User & { full_name?: string };
                    const name =
                      pp.full_name ||
                      `${pp.first_name ?? ''} ${pp.last_name ?? ''}`.trim() ||
                      'Unknown';
                    return (
                      <div className="team-row" key={pp.id ?? i}>
                        <div className="av">{initialsFromName(name)}</div>
                        <div className="who">
                          <span className="name">
                            {name}
                            {i === 0 && <span className="chip gold">Lead</span>}
                          </span>
                          <span className="role">{pp.role || 'Account contact'}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Key details
                </h3>
                <div className="kv">
                  <KvRow
                    label="Tier"
                    value={<span className={`chip ${TIER_TONE[tier]}`}>{tier}</span>}
                  />
                  <KvRow label="Client type" value={client.client_type || '—'} />
                  <KvRow label="Category" value={client.category || '—'} />
                  {client.sub_category && (
                    <KvRow label="Sub-category" value={client.sub_category} />
                  )}
                  <KvRow label="Sector" value={client.sector || '—'} />
                  <KvRow
                    label="Geographic focus"
                    value={client.geographic_focus || client.location || '—'}
                  />
                  <KvRow label="On record since" value={String(since)} mono />
                  {client.excluded_industries && client.excluded_industries.length > 0 && (
                    <KvRow
                      label="Excluded industries"
                      value={<span>{(client.excluded_industries as string[]).join(', ')}</span>}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- Mandates tab — full list of this client's mandates ----- */}
        {tab === 'mandates' && (
          <div className="card flush">
            <div className="card-h">
              <h3>
                Mandates · {clientMandates.length}
                {activeMandates.length > 0 && (
                  <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                    ({activeMandates.length} active)
                  </span>
                )}
              </h3>
              <div className="actions">
                <button
                  className="btn ghost"
                  type="button"
                  onClick={() => setCreatingMandate(true)}
                >
                  <Icon name="plus" size={13} />
                  New mandate
                </button>
              </div>
            </div>
            {clientMandates.length === 0 ? (
              <div className="muted" style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}>
                No mandates yet for {client.name}.
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Project</th>
                    <th>Type</th>
                    <th>Stage</th>
                    <th className="right">Value</th>
                    <th>Target close</th>
                  </tr>
                </thead>
                <tbody>
                  {[...clientMandates]
                    .sort(
                      (a, b) =>
                        new Date(b.signed || b.start_date || 0).getTime() -
                        new Date(a.signed || a.start_date || 0).getTime()
                    )
                    .map((m) => (
                      <tr
                        key={m.id}
                        onClick={() => navigate(`/mandates/${m.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="mono">{deriveId(m)}</td>
                        <td className="strong">{deriveCode(m)}</td>
                        <td>{m.transaction_type || '—'}</td>
                        <td>
                          <StageChip stage={deriveStage(m)} />
                        </td>
                        <td className="num right strong">{formatZar(m.transaction_size)}</td>
                        <td className="mono">{formatDate(m.expected_end_date) || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* --- Tombstones tab — full list of this client's track record - */}
        {tab === 'tombstones' && (
          <div className="card flush">
            <div className="card-h">
              <h3>Track record · {clientTombstones.length}</h3>
              <div className="actions">
                <button
                  className="btn ghost"
                  type="button"
                  onClick={() => setCreatingTombstone(true)}
                >
                  <Icon name="plus" size={13} />
                  New tombstone
                </button>
              </div>
            </div>
            {clientTombstones.length === 0 ? (
              <div className="muted" style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}>
                No tombstones for {client.name} yet. They appear here when a closed deal is logged.
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Deal</th>
                    <th>Type</th>
                    <th>Sector</th>
                    <th className="right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {[...clientTombstones]
                    .sort(
                      (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
                    )
                    .map((t) => (
                      <tr
                        key={t.id}
                        onClick={() => navigate(`/tombstones/${t.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="num">{yearOf(t.date)}</td>
                        <td className="strong">
                          {t.description
                            ? t.description.split('\n')[0].slice(0, 60)
                            : 'Unnamed deal'}
                        </td>
                        <td>
                          <span className="chip gold">{deriveTombstoneType(t)}</span>
                        </td>
                        <td>{t.sub_category || t.category || '—'}</td>
                        <td className="num right strong">{formatZar(t.size_of_transaction)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* --- Other stubbed tabs (Contacts / Documents / Notes) ------- */}
        {tab !== 'overview' && tab !== 'mandates' && tab !== 'tombstones' && (
          <div className="card" style={{ padding: 60, textAlign: 'center' }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Coming soon
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>
              {TABS.find((t) => t.key === tab)?.label} tab
            </div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
              Stubbed in the v3 pilot — the legacy nested route at <code>/clients/:id</code> still
              exposes this data while we migrate.
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClientDetailV3;
